import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTranslation } from '@/contexts/LanguageContext';
import { Lock, CheckCircle } from 'lucide-react';
import { PasswordInput } from '@/components/PasswordInput';

export default function ResetPasswordPage() {
  const { t } = useTranslation('resetPassword');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t('minLength')); return; }
    if (password !== confirm) { toast.error(t('mismatch')); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorExpired'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-neutral-500 mb-4">{t('invalidLink')}</p>
          <Link to="/mot-de-passe-oublie" className="text-sm font-medium text-[#1F3F72] hover:underline">{t('requestNew')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        {done ? (
          <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-8 text-center" data-testid="reset-password-done">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{t('doneTitle')}</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6">{t('doneText')}</p>
            <Button onClick={() => navigate('/connexion')} className="bg-[#1F3F72] text-white" data-testid="go-to-login-btn">
              {t('goToLogin')}
            </Button>
          </div>
        ) : (
          <div className="bg-white dark:bg-neutral-800 border dark:border-neutral-700 rounded-xl p-8">
            <h1 className="font-heading text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">{t('title')}</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">{t('subtitle')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('newPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('newPlaceholder')} className="pl-9" required data-testid="reset-password-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
                  <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t('confirmPlaceholder')} className="pl-9" required data-testid="reset-confirm-input" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-[#1F3F72] text-white" disabled={loading} data-testid="reset-submit-btn">
                {loading ? t('submitting') : t('submitBtn')}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
